import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Edit3, Send, Download, FileText, CalendarDays, Activity, ClipboardList, Stethoscope, Scale, Shield, ShieldCheck, Sliders, MessageSquare, Trash2, Upload, Users, Video, Film, LoaderCircle, Sparkles } from 'lucide-react';
import { CollectabilityBadge, PacketChip } from '../referrals/CollectabilityBadge';
import { TranscriptModal, RecordingModal } from '../telehealth/VisitArtifacts';
import PageLoader from '../shared/PageLoader';
import { format, formatDistanceToNow } from 'date-fns';
import { api } from '../../lib/api';
import { displayEmail, isPlaceholderEmail } from '../../lib/contactEmail';
import { useOrg } from '../../hooks/useOrg';
import './ContactProfile.css';
import { isFirmLike } from '../../lib/orgTypes';

function initialsFor(first, last, email) {
  const a = (first || '').trim()[0] || (email || '').trim()[0] || '?';
  const b = (last || '').trim()[0] || '';
  return (a + b).toUpperCase();
}

function ageFromDob(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  const ms = Date.now() - d.getTime();
  return Math.floor(ms / (365.25 * 24 * 60 * 60 * 1000));
}

const TABS = [
  { key: 'overview',  label: 'Overview',  Icon: Activity },
  { key: 'personal',  label: 'Personal',  Icon: ClipboardList },
  { key: 'case',      label: 'Case',      Icon: FileText },
  { key: 'medical',   label: 'Medical',   Icon: Stethoscope },
  { key: 'legal',     label: 'Legal',     Icon: Scale },
  { key: 'insurance', label: 'Insurance', Icon: Shield },
  { key: 'documents', label: 'Documents', Icon: FileText },
  { key: 'referrals', label: 'Referrals', Icon: ShieldCheck },
  { key: 'visits',    label: 'Telehealth', Icon: Video },
  { key: 'custom',    label: 'Custom',    Icon: Sliders },
  { key: 'notes',     label: 'Notes',     Icon: MessageSquare },
];

// Tabs whose fields sync across partnered orgs (server: SYNCED_FIELDS).
const SYNCED_TABS = new Set(['personal', 'case', 'medical', 'legal', 'insurance']);

// Fields a partnered org shares about a client they also treat/represent.
// Order drives display order in the "From partner organizations" panel.
const PARTNER_FIELD_LABELS = [
  ['shared_notes', 'Notes'],
  ['case_status', 'Case status'],
  ['treatment_status', 'Treatment'],
  ['primary_diagnosis', 'Diagnosis'],
  ['secondary_diagnoses', 'Secondary'],
  ['injury_description', 'Injury'],
  ['injury_severity', 'Severity'],
  ['imaging_completed', 'Imaging'],
  ['referrals_made', 'Referrals'],
  ['accident_date', 'Accident date'],
  ['accident_type', 'Accident type'],
  ['case_number', 'Case #'],
  ['attorney_name', 'Attorney'],
  ['liability_carrier', 'Carrier'],
  ['liability_claim_number', 'Claim #'],
  ['liability_adjuster_name', 'Adjuster'],
  ['liability_adjuster_phone', 'Adjuster phone'],
];

const CASE_STATUS_PILL = {
  intake:      { cls: 'info',    label: 'Intake' },
  treatment:   { cls: 'info',    label: 'In treatment' },
  demand:      { cls: 'warning', label: 'Demand' },
  negotiation: { cls: 'warning', label: 'Negotiation' },
  litigation: { cls: 'danger',  label: 'In litigation' },
  settled:     { cls: 'success', label: 'Settled' },
  closed:      { cls: 'muted',   label: 'Closed' },
};

const TREATMENT_PILL = {
  active:     { cls: 'info',    label: 'Active treatment' },
  mmi:        { cls: 'warning', label: 'MMI' },
  discharged: { cls: 'success', label: 'Discharged' },
  referred:   { cls: 'info',    label: 'Referred out' },
};

// One generic field renderer for both built-in and custom fields.
function Field({ label, type = 'text', value, options, placeholder, helpText, onChange, wide, rows }) {
  const v = value == null ? '' : value;
  const baseProps = { className: 'cp-input', value: v, placeholder: placeholder || '', onChange: (e) => onChange(e.target.value) };
  let input;
  if (type === 'textarea') {
    input = <textarea {...baseProps} className="cp-textarea" rows={rows || 4} />;
  } else if (type === 'select') {
    input = (
      <select {...baseProps} className="cp-select">
        <option value="">— select —</option>
        {(options || []).map(o => (
          <option key={o.value || o} value={o.value || o}>{o.label || o.value || o}</option>
        ))}
      </select>
    );
  } else if (type === 'multi_select') {
    const arr = Array.isArray(value) ? value : [];
    input = (
      <select multiple value={arr} className="cp-select" onChange={(e) => {
        const sel = Array.from(e.target.selectedOptions, o => o.value);
        onChange(sel);
      }}>
        {(options || []).map(o => (
          <option key={o.value || o} value={o.value || o}>{o.label || o.value || o}</option>
        ))}
      </select>
    );
  } else if (type === 'boolean') {
    input = (
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 0', fontSize: 13 }}>
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        <span>Yes</span>
      </label>
    );
  } else if (type === 'date') {
    input = <input type="date" {...baseProps} />;
  } else if (type === 'number') {
    input = <input type="number" step="any" {...baseProps} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />;
  } else {
    input = <input type={type} {...baseProps} />;
  }
  return (
    <div className={`cp-field ${wide ? 'is-wide' : ''}`}>
      <label className="cp-label">{label}</label>
      {input}
      {helpText && <div className="cp-help">{helpText}</div>}
    </div>
  );
}

export default function ContactProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentOrg } = useOrg();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Honor ?tab= on entry so pages can deep-link back to a specific tab
  // (the concierge returns to /app/contacts/:id?tab=referrals).
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return t && TABS.some(x => x.key === t) ? t : 'overview';
  });
  const [profileEdits, setProfileEdits] = useState({});
  const [customEdits, setCustomEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [inviting, setInviting] = useState(false);

  const [docs, setDocs] = useState(null);
  const [visits, setVisits] = useState(null);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [visitTranscript, setVisitTranscript] = useState(null);
  const [visitRecording, setVisitRecording] = useState(null);
  const [docsLoading, setDocsLoading] = useState(false);
  const [clientCases, setClientCases] = useState(null); // this person's matters (cases table), lazy on the Case tab
  const [clientReferrals, setClientReferrals] = useState(null); // real referrals for this person, lazy on the Referrals tab
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  const refresh = useCallback(() => {
    if (!currentOrg) return;
    setLoading(true);
    api.getClientProfile(currentOrg.id, id)
      .then(d => { setData(d); setProfileEdits({}); setCustomEdits({}); setError(null); })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [currentOrg, id]);

  useEffect(() => { refresh(); }, [refresh]);

  // Lazy-load documents the first time the tab opens. Cached in state so
  // tab-flipping doesn't re-fetch.
  useEffect(() => {
    if (tab !== 'documents' || docs !== null || !currentOrg) return;
    setDocsLoading(true);
    api.getClientDocuments(currentOrg.id, id)
      .then(setDocs)
      .catch(err => setError(err.message))
      .finally(() => setDocsLoading(false));
  }, [tab, docs, currentOrg, id]);

  useEffect(() => {
    if (tab !== 'case' || clientCases !== null || !currentOrg) return;
    api.listCases(currentOrg.id)
      .then(rows => setClientCases((rows || []).filter(r => r.client_id === id)))
      .catch(() => setClientCases([]));
  }, [tab, clientCases, currentOrg, id]);

  useEffect(() => {
    if (tab !== 'referrals' || clientReferrals !== null || !currentOrg) return;
    api.listReferrals(currentOrg.id)
      .then(rows => setClientReferrals((rows || []).filter(r => r.client_id === id)))
      .catch(err => { setError(err.message); setClientReferrals([]); });
  }, [tab, clientReferrals, currentOrg, id]);

  // Video visits, same lazy pattern: this client's ended sessions with
  // their transcripts and recordings.
  useEffect(() => {
    if (tab !== 'visits' || visits !== null || !currentOrg) return;
    setVisitsLoading(true);
    api.getTelehealthHistory(currentOrg.id, { client_id: id })
      .then(rows => setVisits((rows || []).filter(r => r.status === 'ended')))
      .catch(err => setError(err.message))
      .finally(() => setVisitsLoading(false));
  }, [tab, visits, currentOrg, id]);

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file || !currentOrg) return;
    setUploading(true); setUploadError('');
    try {
      await api.uploadDocument(currentOrg.id, file, null, id);
      setDocs(null); // triggers the lazy-load effect to re-fetch
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const openUploadedFile = async (docId) => {
    try {
      const { url } = await api.getDocumentDownloadUrl(currentOrg.id, docId);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      setUploadError(err.message);
    }
  };

  const dirty = Object.keys(profileEdits).length > 0 || Object.keys(customEdits).length > 0;
  const partnerRecords = data?.partner_records || [];

  const setField = (k, v) => setProfileEdits(p => ({ ...p, [k]: v }));
  const cur = (k) => profileEdits[k] !== undefined ? profileEdits[k] : data?.profile?.[k];

  const setCustom = (k, v) => setCustomEdits(p => ({ ...p, [k]: v }));
  const curCustom = (k) => customEdits[k] !== undefined ? customEdits[k] : (data?.profile?.custom_data?.[k] ?? '');

  const handleSave = async () => {
    setSaving(true); setSavedMsg('');
    try {
      const res = await api.updateClientProfile(currentOrg.id, id, {
        profile: profileEdits,
        custom_data: Object.keys(customEdits).length ? customEdits : undefined,
      });
      const n = res?.synced_to || 0;
      setSavedMsg(n ? `Saved · synced to ${n} partner organization${n === 1 ? '' : 's'}` : 'Saved');
      refresh();
      setTimeout(() => setSavedMsg(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const [exporting, setExporting] = useState(false);
  const exportRecord = async () => {
    setExporting(true);
    try { await api.exportPatientRecord(currentOrg.id, id); } catch (e) { window.alert(e.message || 'Export failed'); } finally { setExporting(false); }
  };
  const handleInvite = async () => {
    setInviting(true);
    try {
      await api.inviteContact(currentOrg.id, id);
      setSavedMsg('Invitation email sent');
      setTimeout(() => setSavedMsg(''), 4000);
    } catch (err) { setError(err.message); }
    finally { setInviting(false); }
  };

  // Group custom fields by section for the Custom tab
  const customSections = useMemo(() => {
    const fields = data?.custom_fields || [];
    const groups = {};
    for (const f of fields) {
      const s = f.section || 'Custom';
      if (!groups[s]) groups[s] = [];
      groups[s].push(f);
    }
    return Object.entries(groups);
  }, [data]);

  if (loading) {
    return (
      <div className="cp">
        <PageLoader variant="block" sub="Client" label="Loading profile…" />
      </div>
    );
  }
  if (error && !data) {
    return <div className="cp"><div className="cp-banner danger">{error}</div></div>;
  }
  if (!data) return null;

  const { user, profile, stats, member_since } = data;
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ') || displayEmail(user.email, 'Client');
  const noEmail = isPlaceholderEmail(user.email);
  const age = ageFromDob(profile.date_of_birth);
  const casePill  = profile.case_status      && CASE_STATUS_PILL[profile.case_status];
  const treatPill = profile.treatment_status && TREATMENT_PILL[profile.treatment_status];

  return (
    <div className="cp">
      <button className="cp-back" onClick={() => navigate('/app/contacts')}>
        <ArrowLeft size={12} /> All clients
      </button>

      {/* Hero */}
      <div className="cp-hero">
        <div className="cp-avatar">{initialsFor(user.first_name, user.last_name, user.email)}</div>
        <div className="cp-meta">
          <div className="cp-pills">
            {casePill && <span className={`cp-pill ${casePill.cls}`}>{casePill.label}</span>}
            {treatPill && <span className={`cp-pill ${treatPill.cls}`}>{treatPill.label}</span>}
            {profile.accident_type && <span className="cp-pill muted">{profile.accident_type.replace('_', ' ')}</span>}
            {!user.is_active && <span className="cp-pill danger">Account inactive</span>}
          </div>
          <h1 className="cp-name">{fullName}</h1>
          <div className="cp-sub">
            {age != null && <span>{age} yrs</span>}
            {user.email && <span>{noEmail ? 'No email on file' : user.email}</span>}
            {user.phone && <span>{user.phone}</span>}
            {member_since && <span>Client since {formatDistanceToNow(new Date(member_since), { addSuffix: true })}</span>}
          </div>
        </div>
        <div className="cp-hero-actions">
          <button className="cp-btn cp-btn-secondary" onClick={() => setTab('personal')}>
            <Edit3 size={12} /> Edit
          </button>
          <button className="cp-btn cp-btn-secondary" onClick={exportRecord} disabled={exporting} title="Download this patient's complete record: documents, signed forms, visits, referrals, cases and notes (ZIP)">
            {exporting ? <LoaderCircle size={12} className="animate-spin" /> : <Download size={12} />} {exporting ? 'Preparing…' : 'Export record'}
          </button>
          <button className="cp-btn cp-btn-primary" onClick={handleInvite} disabled={inviting || noEmail} title={noEmail ? 'Add an email under Edit to invite this client to the portal' : undefined}>
            <Send size={12} /> {inviting ? 'Sending…' : noEmail ? 'Add email to invite' : 'Send invite'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="cp-stats">
        <div className="cp-stat">
          <div className="cp-stat-label">Appointments</div>
          <div className="cp-stat-value">{stats.appointments_total}</div>
        </div>
        <div className="cp-stat">
          <div className="cp-stat-label">Forms signed</div>
          <div className="cp-stat-value">{stats.forms_signed}</div>
        </div>
        <div className="cp-stat">
          <div className="cp-stat-label">Settlement</div>
          <div className="cp-stat-value">{profile.settlement_amount ? `$${Number(profile.settlement_amount).toLocaleString()}` : '—'}</div>
        </div>
        <div className="cp-stat">
          <div className="cp-stat-label">Case status</div>
          <div className="cp-stat-value" style={{ fontSize: 18 }}>{casePill?.label || profile.case_status || '—'}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="cp-tabs">
        {TABS.map(t => (
          <button key={t.key} className={`cp-tab ${tab === t.key ? 'is-active' : ''}`} onClick={() => setTab(t.key)}>
            <t.Icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {savedMsg && <div className="cp-banner success">{savedMsg}</div>}
      {error && <div className="cp-banner danger">{error}</div>}

      {/* Shared-record notice on the fact-bearing tabs */}
      {partnerRecords.length > 0 && SYNCED_TABS.has(tab) && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 9,
          background: 'rgba(13,167,202,0.07)', border: '1px solid rgba(13,167,202,0.20)',
          borderRadius: 10, padding: '10px 13px', marginBottom: 16,
          fontSize: 12, lineHeight: 1.55, color: 'var(--ink)',
        }}>
          <Users size={14} style={{ color: '#0DA7CA', flexShrink: 0, marginTop: 1 }} />
          <span>
            <strong>Shared record.</strong> Changes here sync automatically with{' '}
            {partnerRecords.map(r => r.org?.name).filter(Boolean).join(', ')}.
            {tab === 'legal' && ' Settlement, fee and lien figures stay private to your organization.'}
            {tab === 'case' && ' Your case/file number stays private to your organization.'}
          </span>
        </div>
      )}

      {/* Overview */}
      {tab === 'overview' && (
        <>
          <div className="cp-card">
            <h2 className="cp-card-title">Quick facts</h2>
            <div className="cp-form">
              <Field label="Date of birth" type="date" value={cur('date_of_birth')} onChange={v => setField('date_of_birth', v)} />
              <Field label="Phone" type="phone" value={cur('emergency_contact_phone')} onChange={v => setField('emergency_contact_phone', v)} placeholder="—" />
              <Field label="Accident date" type="date" value={cur('accident_date')} onChange={v => setField('accident_date', v)} />
              <Field label="Accident type" type="select" value={cur('accident_type')} onChange={v => setField('accident_type', v)}
                options={[{ value: 'mva', label: 'Motor vehicle' }, { value: 'slip_fall', label: 'Slip & fall' }, { value: 'workers_comp', label: 'Workers comp' }, { value: 'premises', label: 'Premises' }, { value: 'other', label: 'Other' }]} />
              <Field label="Case status" type="select" value={cur('case_status')} onChange={v => setField('case_status', v)}
                options={Object.entries(CASE_STATUS_PILL).map(([k, v]) => ({ value: k, label: v.label }))} />
              <Field label="Treatment status" type="select" value={cur('treatment_status')} onChange={v => setField('treatment_status', v)}
                options={Object.entries(TREATMENT_PILL).map(([k, v]) => ({ value: k, label: v.label }))} />
            </div>
          </div>
          <div className="cp-card">
            <h2 className="cp-card-title">Recent activity</h2>
            <div className="cp-empty">
              <Activity size={28} />
              <div>Activity feed coming soon — appointments, signed forms, document shares.</div>
            </div>
          </div>
        </>
      )}

      {/* Personal */}
      {tab === 'personal' && (
        <div className="cp-card">
          <h2 className="cp-card-title">Personal information</h2>
          <div className="cp-form cp-form-3">
            <Field label="Date of birth" type="date" value={cur('date_of_birth')} onChange={v => setField('date_of_birth', v)} />
            <Field label="Gender" value={cur('gender')} onChange={v => setField('gender', v)} />
            <Field label="Marital status" value={cur('marital_status')} onChange={v => setField('marital_status', v)} />
            <Field label="Occupation" value={cur('occupation')} onChange={v => setField('occupation', v)} />
            <Field label="Employer" value={cur('employer')} onChange={v => setField('employer', v)} />
            <Field label="Preferred language" value={cur('preferred_language')} onChange={v => setField('preferred_language', v)} placeholder="English" />
            <div className="cp-section-divider">Address</div>
            <Field wide label="Street" value={cur('address_line1')} onChange={v => setField('address_line1', v)} />
            <Field label="Address line 2" value={cur('address_line2')} onChange={v => setField('address_line2', v)} />
            <Field label="City" value={cur('city')} onChange={v => setField('city', v)} />
            <Field label="State" value={cur('state')} onChange={v => setField('state', v)} />
            <Field label="ZIP" value={cur('zip')} onChange={v => setField('zip', v)} />
            <div className="cp-section-divider">Emergency contact</div>
            <Field label="Name" value={cur('emergency_contact_name')} onChange={v => setField('emergency_contact_name', v)} />
            <Field label="Phone" value={cur('emergency_contact_phone')} onChange={v => setField('emergency_contact_phone', v)} />
            <Field label="Relationship" value={cur('emergency_contact_relationship')} onChange={v => setField('emergency_contact_relationship', v)} />
          </div>
        </div>
      )}

      {/* Case */}
      {tab === 'case' && (
        <div className="cp-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <h2 className="cp-card-title" style={{ marginBottom: 0 }}>Cases</h2>
            {isFirmLike(currentOrg?.type) && (
              <Link to={`/app/cases/new?client=${id}`} className="cp-btn cp-btn-primary"><Sparkles size={12} /> New case</Link>
            )}
          </div>
          <div style={{ marginTop: 12 }}>
            {clientCases === null ? (
              <div className="cp-help">Loading…</div>
            ) : clientCases.length === 0 ? (
              <div className="cp-help">No case on file for this person yet.</div>
            ) : clientCases.map(cs => (
              <Link key={cs.id} to={`/app/cases/${cs.id}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 8, textDecoration: 'none', color: 'inherit' }}>
                <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, fontWeight: 600 }}>{cs.case_number}</span>
                <span className={`cp-pill ${['settled', 'closed'].includes(cs.status) ? 'muted' : 'info'}`}>{CASE_STATUS_PILL[cs.status]?.label || cs.status}</span>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                  {cs.case_type === 'auto_mva' ? 'Auto (MVA)' : cs.case_type === 'premises' ? 'Premises' : 'Other PI'}
                  {cs.accident_date ? ` · injured ${format(new Date(`${cs.accident_date}T12:00:00`), 'MMM d, yyyy')}` : ''}
                  {cs.funding?.current?.chip ? ` · ${cs.funding.current.chip}` : ''}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
      {tab === 'case' && (
        <div className="cp-card">
          <h2 className="cp-card-title">Case information</h2>
          <div className="cp-form">
            <Field label="Case number" value={cur('case_number')} onChange={v => setField('case_number', v)} />
            <Field label="Accident date" type="date" value={cur('accident_date')} onChange={v => setField('accident_date', v)} />
            <Field label="Accident type" type="select" value={cur('accident_type')} onChange={v => setField('accident_type', v)}
              options={[{ value: 'mva', label: 'Motor vehicle' }, { value: 'slip_fall', label: 'Slip & fall' }, { value: 'workers_comp', label: 'Workers comp' }, { value: 'premises', label: 'Premises' }, { value: 'other', label: 'Other' }]} />
            <Field label="Statute of limitations" type="date" value={cur('statute_of_limitations_date')} onChange={v => setField('statute_of_limitations_date', v)} />
            <Field wide label="Accident location" value={cur('accident_location')} onChange={v => setField('accident_location', v)} placeholder="123 Main St, Miami FL" />
            <Field wide label="Accident description" type="textarea" value={cur('accident_description')} onChange={v => setField('accident_description', v)} />
            <Field wide label="Injury description" type="textarea" value={cur('injury_description')} onChange={v => setField('injury_description', v)} />
            <Field label="Injury severity" type="select" value={cur('injury_severity')} onChange={v => setField('injury_severity', v)}
              options={[{ value: 'mild', label: 'Mild' }, { value: 'moderate', label: 'Moderate' }, { value: 'severe', label: 'Severe' }]} />
            <Field label="Case status" type="select" value={cur('case_status')} onChange={v => setField('case_status', v)}
              options={Object.entries(CASE_STATUS_PILL).map(([k, v]) => ({ value: k, label: v.label }))} />
          </div>
        </div>
      )}

      {/* Medical */}
      {tab === 'medical' && (
        <div className="cp-card">
          <h2 className="cp-card-title">Medical</h2>
          <div className="cp-form">
            <Field label="Treatment status" type="select" value={cur('treatment_status')} onChange={v => setField('treatment_status', v)}
              options={Object.entries(TREATMENT_PILL).map(([k, v]) => ({ value: k, label: v.label }))} />
            <Field label="Primary diagnosis" value={cur('primary_diagnosis')} onChange={v => setField('primary_diagnosis', v)} />
            <Field wide label="Secondary diagnoses (comma-separated)"
              value={Array.isArray(cur('secondary_diagnoses')) ? cur('secondary_diagnoses').join(', ') : ''}
              onChange={v => setField('secondary_diagnoses', v ? v.split(',').map(s => s.trim()).filter(Boolean) : [])} />
            <Field wide label="Imaging completed (comma-separated)"
              value={Array.isArray(cur('imaging_completed')) ? cur('imaging_completed').join(', ') : ''}
              onChange={v => setField('imaging_completed', v ? v.split(',').map(s => s.trim()).filter(Boolean) : [])}
              helpText="e.g. X-Ray cervical spine, MRI lumbar, CT head" />
            <Field wide label="Referrals made (comma-separated)"
              value={Array.isArray(cur('referrals_made')) ? cur('referrals_made').join(', ') : ''}
              onChange={v => setField('referrals_made', v ? v.split(',').map(s => s.trim()).filter(Boolean) : [])}
              helpText="e.g. Orthopedic surgeon, Pain management" />
          </div>
        </div>
      )}

      {/* Legal */}
      {tab === 'legal' && (
        <div className="cp-card">
          <h2 className="cp-card-title">Legal</h2>
          <div className="cp-form">
            <Field label="Attorney name" value={cur('attorney_name')} onChange={v => setField('attorney_name', v)} />
            <Field label="Case status" type="select" value={cur('case_status')} onChange={v => setField('case_status', v)}
              options={Object.entries(CASE_STATUS_PILL).map(([k, v]) => ({ value: k, label: v.label }))} />
            <Field label="Settlement amount" type="number" value={cur('settlement_amount')} onChange={v => setField('settlement_amount', v)} />
            <Field label="Fee percentage" type="number" value={cur('fee_percentage')} onChange={v => setField('fee_percentage', v)} placeholder="33.33" />
            <Field label="Lien holder count" type="number" value={cur('lien_holder_count')} onChange={v => setField('lien_holder_count', v)} />
            <Field label="Total lien amount" type="number" value={cur('total_lien_amount')} onChange={v => setField('total_lien_amount', v)} />
          </div>
        </div>
      )}

      {/* Insurance */}
      {tab === 'insurance' && (
        <div className="cp-card">
          <h2 className="cp-card-title">Insurance</h2>
          <div className="cp-form">
            <div className="cp-section-divider">Liability (at-fault party)</div>
            <Field label="Liability carrier" value={cur('liability_carrier')} onChange={v => setField('liability_carrier', v)} />
            <Field label="Claim number" value={cur('liability_claim_number')} onChange={v => setField('liability_claim_number', v)} />
            <Field label="Adjuster name" value={cur('liability_adjuster_name')} onChange={v => setField('liability_adjuster_name', v)} />
            <Field label="Adjuster phone" value={cur('liability_adjuster_phone')} onChange={v => setField('liability_adjuster_phone', v)} />
            <Field label="BI limits" value={cur('liability_bi_limits')} onChange={v => setField('liability_bi_limits', v)} placeholder="$25k / $50k" />
            <Field label="UM/UIM limits" value={cur('um_uim_limits')} onChange={v => setField('um_uim_limits', v)} />
            <Field label="MedPay / PIP" type="number" value={cur('medpay_pip_amount')} onChange={v => setField('medpay_pip_amount', v)} />
            <div className="cp-section-divider">Health insurance</div>
            <Field label="Carrier" value={cur('health_insurance_carrier')} onChange={v => setField('health_insurance_carrier', v)} />
            <Field label="Member ID" value={cur('health_member_id')} onChange={v => setField('health_member_id', v)} />
            <Field label="Group ID" value={cur('health_group_id')} onChange={v => setField('health_group_id', v)} />
          </div>
        </div>
      )}

      {/* Documents — files uploaded to this client + forms they've signed */}
      {tab === 'documents' && (
        <div className="cp-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <h2 className="cp-card-title" style={{ marginBottom: 0 }}>Documents</h2>
            <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileSelected} />
            <button
              className="cp-btn cp-btn-primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}
            >
              <Upload size={14} />
              {uploading ? 'Uploading…' : 'Upload document'}
            </button>
          </div>
          {uploadError && (
            <div style={{ fontSize: 12, color: 'var(--danger, #f43f5e)', margin: '8px 0' }}>{uploadError}</div>
          )}
          {docsLoading || (docs === null && !error) ? (
            <PageLoader variant="block" sub="Documents" label="Loading client files…" />
          ) : !docs?.signed_forms?.length && !docs?.files?.length ? (
            <div className="cp-empty">
              <FileText size={28} />
              <div>No documents yet.<br />Upload files here, or have the client sign a form.</div>
            </div>
          ) : (
            <div>
              {(docs.files || []).map((d, i) => (
                <div key={d.id} style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '14px 4px',
                  borderTop: i > 0 ? '1px solid var(--line)' : 'none',
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: 'rgba(139,92,246,0.10)', color: '#8b5cf6',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Upload size={16} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</span>
                      {d.from_partner && (
                        <span style={{
                          fontSize: 10, fontWeight: 700, color: '#0DA7CA', flexShrink: 0,
                          background: 'rgba(13,167,202,0.10)', padding: '2px 8px', borderRadius: 999,
                        }}>
                          from {d.source_org_name}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                      Uploaded {format(new Date(d.created_at), 'MMM d, yyyy')}
                      {d.uploader ? ` by ${d.uploader.first_name} ${d.uploader.last_name}` : ''}
                      {d.size_bytes ? ` · ${(d.size_bytes / 1024 / 1024).toFixed(1)} MB` : ''}
                    </div>
                  </div>
                  <button
                    onClick={() => openUploadedFile(d.id)}
                    className="cp-btn cp-btn-secondary"
                    style={{ flexShrink: 0 }}
                  >
                    Open
                  </button>
                </div>
              ))}
              {docs.signed_forms.map((d, i) => {
                const signedDate = d.signed_at ? format(new Date(d.signed_at), 'MMM d, yyyy') : null;
                return (
                  <div key={d.id} style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '14px 4px',
                    borderTop: (i > 0 || (docs.files || []).length > 0) ? '1px solid var(--line)' : 'none',
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 10,
                      background: 'rgba(13,167,202,0.10)', color: 'var(--teal)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <FileText size={16} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{d.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                        {d.status === 'signed'
                          ? `Signed ${signedDate || '—'}`
                          : `Status: ${d.status}`}
                      </div>
                    </div>
                    {d.document_url && (
                      <a
                        href={d.document_url}
                        target="_blank" rel="noopener noreferrer"
                        className="cp-btn cp-btn-secondary"
                        style={{ flexShrink: 0, textDecoration: 'none' }}
                      >
                        Open
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Referrals — the real referral records for this person */}
      {tab === 'referrals' && (
        <div className="cp-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, gap: 12, flexWrap: 'wrap' }}>
            <h2 className="cp-card-title" style={{ marginBottom: 0 }}>Referrals</h2>
            {isFirmLike(currentOrg?.type) && (
              <button
                className="cp-btn cp-btn-primary"
                onClick={() => navigate(`/app/referrals/concierge?client=${id}&return=${encodeURIComponent(`/app/contacts/${id}?tab=referrals`)}`)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}
              >
                <Sparkles size={14} /> Find a provider
              </button>
            )}
          </div>
          {clientReferrals === null && <div className="cp-help">Loading…</div>}
          {clientReferrals?.length === 0 && (
            <div className="cp-empty">
              <ShieldCheck size={28} />
              <div>No referrals for {user.first_name || 'this client'} yet.</div>
            </div>
          )}
          {clientReferrals?.map((r, i) => {
            const status = r.status || 'received';
            const statusLabel = { received: 'Awaiting acceptance', accepted: 'Accepted — patient choosing a time', scheduled: 'First visit scheduled', completed: 'Care completed', delivered: 'Records delivered', declined: 'Declined', expired: 'Expired — no answer' }[status] || status;
            return (
              <div
                key={r.id}
                onClick={() => navigate('/app/referrals')}
                style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', padding: '14px 4px', cursor: 'pointer', borderTop: i > 0 ? '1px solid var(--line)' : 'none' }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }} className="bg-accent-glow text-accent-light">
                  <ShieldCheck size={16} />
                </div>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }} className="text-primary-c">{r.service || 'Referral'}</div>
                  <div style={{ fontSize: 11 }} className="text-muted-c">
                    {isFirmLike(currentOrg?.type) ? `To ${r.provider?.name || 'provider'}` : `From ${r.firm?.name || 'law firm'}`}
                    {' · '}{r.created_at ? format(new Date(r.created_at), 'MMM d, yyyy') : ''}
                    {r.via === 'patient' ? ' · requested by the patient' : ''}
                  </div>
                </div>
                <PacketChip referral={r} />
                <CollectabilityBadge referral={r} />
                <span style={{ fontSize: 11, fontWeight: 600 }} className="text-secondary-c">{statusLabel}</span>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'visits' && (
        <div className="cp-card">
          <h2 className="cp-card-title">Video visits</h2>
          {visitsLoading && (
            <p className="text-[12px] text-muted-c flex items-center gap-2 py-4"><LoaderCircle size={14} className="animate-spin" /> Loading visits…</p>
          )}
          {!visitsLoading && (visits?.length ?? 0) === 0 && (
            <div className="cp-empty">No video visits with this client yet. Start one from their appointment in Scheduling.</div>
          )}
          {visits?.map(v => {
            const mins = v.started_at && v.ended_at
              ? Math.max(1, Math.round((new Date(v.ended_at) - new Date(v.started_at)) / 60000))
              : null;
            return (
              <div key={v.id} className="flex flex-col sm:flex-row sm:items-center gap-3 py-3 border-b border-theme last:border-b-0">
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-primary-c">
                    {new Date(v.started_at || v.created_at).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                    {' · '}
                    {new Date(v.started_at || v.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                  </div>
                  <div className="text-[11px] text-muted-c mt-0.5">
                    {v.appointment?.title || 'Video visit'}{mins ? ` · ${mins} min` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {v.utterance_count > 0 && (
                    <button onClick={() => setVisitTranscript(v)}
                      className="px-3 py-[7px] rounded-lg text-[11px] font-semibold text-accent-light bg-accent-glow border border-strong hover:bg-hover-surface transition-all flex items-center gap-1.5">
                      <FileText size={12} /> Transcript
                    </button>
                  )}
                  {(v.recording_status === 'ready' || v.recording_status === 'processing') && (
                    <button onClick={() => setVisitRecording(v)}
                      className="px-3 py-[7px] rounded-lg text-[11px] font-semibold text-accent-light bg-accent-glow border border-strong hover:bg-hover-surface transition-all flex items-center gap-1.5">
                      <Film size={12} /> {v.recording_status === 'processing' ? 'Recording…' : 'Recording'}
                    </button>
                  )}
                  {v.utterance_count === 0 && v.recording_status === 'none' && (
                    <span className="text-[11px] text-muted-c">No artifacts</span>
                  )}
                </div>
              </div>
            );
          })}
          {visitTranscript && <TranscriptModal session={visitTranscript} onClose={() => setVisitTranscript(null)} />}
          {visitRecording && <RecordingModal session={visitRecording} onClose={() => setVisitRecording(null)} />}
        </div>
      )}

      {/* Custom fields */}
      {tab === 'custom' && (
        <div className="cp-card">
          <h2 className="cp-card-title">Custom fields</h2>
          {customSections.length === 0 ? (
            <div className="cp-empty">
              <Sliders size={28} />
              <div>No custom fields defined yet. Add some in Settings → Client fields.</div>
            </div>
          ) : (
            customSections.map(([section, fields]) => (
              <div key={section} style={{ marginBottom: 24 }}>
                <div className="cp-section-divider" style={{ borderTop: 'none', paddingTop: 0, marginTop: 0, marginBottom: 12 }}>{section}</div>
                <div className="cp-form">
                  {fields.map(f => (
                    <Field
                      key={f.id}
                      label={f.label + (f.required ? ' *' : '')}
                      type={f.type}
                      options={f.options}
                      placeholder={f.placeholder}
                      helpText={f.help_text}
                      value={curCustom(f.key)}
                      onChange={(v) => setCustom(f.key, v)}
                      wide={f.type === 'textarea'}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Notes */}
      {tab === 'notes' && (
        <>
          <div className="cp-card">
            <h2 className="cp-card-title">Shared notes</h2>
            <Field
              label={`Visible to partner organizations treating or representing ${data.user?.first_name || 'this client'}`}
              type="textarea"
              value={cur('shared_notes')}
              onChange={v => setField('shared_notes', v)}
              wide
              rows={6}
            />
          </div>

          {partnerRecords.length > 0 && (
            <div className="cp-card" style={{ marginTop: 20 }}>
              <h2 className="cp-card-title">From partner organizations</h2>
              {partnerRecords.map(rec => {
                const f = rec.fields || {};
                const updates = PARTNER_FIELD_LABELS
                  .filter(([key]) => key !== 'shared_notes' && f[key] != null)
                  .map(([key, label]) => [label, Array.isArray(f[key]) ? f[key].join(', ') : String(f[key])]);
                return (
                  <div key={rec.org_id} style={{ marginBottom: 18 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: 12, fontWeight: 700,
                        color: rec.org?.type === 'law_firm' ? '#8b5cf6' : '#0DA7CA',
                      }}>
                        {rec.org?.name}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                        {rec.org?.type === 'law_firm' ? 'Law firm' : 'Medical practice'}
                        {f.updated_at ? ` · updated ${formatDistanceToNow(new Date(f.updated_at), { addSuffix: true })}` : ''}
                      </span>
                    </div>
                    {f.shared_notes && (
                      <div style={{
                        fontSize: 13, lineHeight: 1.6, color: 'var(--ink)', whiteSpace: 'pre-wrap',
                        background: 'var(--paper)', border: '1px solid var(--line)',
                        borderRadius: 10, padding: '12px 14px', marginBottom: updates.length ? 10 : 0,
                      }}>
                        {f.shared_notes}
                      </div>
                    )}
                    {updates.length > 0 && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '6px 18px' }}>
                        {updates.map(([label, value]) => (
                          <div key={label} style={{ fontSize: 12.5 }}>
                            <span style={{ color: 'var(--muted)' }}>{label}: </span>
                            <span style={{ color: 'var(--ink)', fontWeight: 500 }}>{value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                Read-only. Each organization edits its own record; shared notes and case details appear here automatically.
              </p>
            </div>
          )}

          <div className="cp-card" style={{ marginTop: 20 }}>
            <h2 className="cp-card-title">Internal notes</h2>
            <Field
              label="Notes (visible only to your org — never shared with partners)"
              type="textarea"
              value={cur('internal_notes')}
              onChange={v => setField('internal_notes', v)}
              wide
              rows={8}
            />
          </div>
        </>
      )}

      {dirty && (
        <div className="cp-save-bar">
          <span className="cp-dirty-msg">Unsaved changes</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="cp-btn cp-btn-secondary" onClick={() => { setProfileEdits({}); setCustomEdits({}); }}>
              Discard
            </button>
            <button className="cp-btn cp-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
